import { AssetEmbedType } from 'library/ts/model/enums/content/AssetEmbedType';
import { HTML5AdParameterBuilder } from 'tag/ts/tag/ads/freewheel/builders/HTML5AdParameterBuilder';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { AbstractResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/abstract/AbstractResourceConfigBuilder';
import { STVPlatformConfigs } from 'tag/ts/tag/player/stv/configs/STVPlatformConfigs';
import { STVUtilities } from 'tag/ts/tag/utilities/STVUtilities';
import { TagContentUtilities } from 'tag/ts/tag/utilities/TagContentUtilities';
export class AbstractHTML5ResourceConfigBuilder extends AbstractResourceConfigBuilder {
    html5ExcludedFMSKeys;
    #contentUtilities;
    constructor(argument) {
        super({
            name: 'AbstractHTML5ResourceConfigBuilder',
            ...argument
        });
        this.#contentUtilities = new TagContentUtilities({ tag: this.tag });
        this.html5ExcludedFMSKeys = [
            'correlator',
            'imafw__fw_app_bundle',
            'imafw__fw_h_referer',
            'imafw_cust_params',
            'imafw_description_url',
            'imafw_ifa',
            'imafw_nextShowsIdsArray',
            'imafw_rdid',
            'imafw_streamingUrl',
            'imafw_playername_version',
            'imafw__fw_did',
            'imafw_fms_userid',
            'imafw_npa',
            ...(this.#contentUtilities?.isKidsContent() ? ['imafw_sub'] : [])
        ];
    }
    getResult() {
        return new Promise((resolve, reject) => {
            const resource = window.avia.createResourceConfig();
            this?.logger?.log('getResult', 'created instance of ResourceConfigurationInterface using avia:', resource);
            const promiseArray = [
                () => this.tag.model.siteUtilities.getSiteSessionData(this.name),
                () => this.setStartBitrate(resource),
                () => this.setContextMetadata(resource),
                () => this.setOverrides(resource)
            ];
            this.utils.Function.runPromiseArray(promiseArray).then(() => {
                resolve({
                    msg: 'Resource config',
                    resource: resource
                });
            });
        });
    }
    setContextMetadata = (resource) => {
        const setDeviceFramework = (resource) => {
            resource.metadata.deviceCategory = 'HTML5';
            resource.metadata.frameworkName = 'SmartTag';
            resource.metadata.frameworkVersion = window?.PlayerServicesProjectInfo?.release_version;
            return resource;
        };
        return new Promise((resolve, reject) => {
            if (resource.metadata === undefined)
                resource.metadata = {};
            if (!this.utils.Type.isEmpty(this.params?.CONTEXT?.metadata)
                &&
                    this.utils.Type.isObject(this.params?.CONTEXT?.metadata)) {
                Object.keys(this.params?.CONTEXT?.metadata).forEach((key) => {
                    resource.metadata[key] = this.params.CONTEXT.metadata[key];
                });
            }
            resource = setDeviceFramework(resource);
            switch (this.tag.model.contentUtilities.assetEmbedType(this.params)) {
                case AssetEmbedType.ASSET_URL:
                case AssetEmbedType.PID:
                    resource.metadata = this.apiMetadata;
                    break;
                case AssetEmbedType.CID:
                    break;
                default:
            }
            this?.logger?.log('setContext:', resource);
            resolve(resource);
        });
    };
    setPlatformSpecificResource = (resource) => {
        const platformSpecificResourceConfig = new STVPlatformConfigs({ tag: this.tag });
        this?.logger?.log('setPlatformSpecificResource', 'Platform Configs:', platformSpecificResourceConfig.resourceConfig);
        resource = this.utils.DeepMerge.deepmerge(resource, platformSpecificResourceConfig.resourceConfig);
        return resource;
    };
    setAdParameters(resource) {
        const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();
        switch (adSetupInfo?.setup?.definition) {
            case AdSetupDefinition.DAI_API:
                resource = this.setDaiApiAdParameters(resource);
                resource = this.setEnableNonce(resource, this.tag.tagUtilities.isInternationalContext(this.tag));
                break;
        }
        if (this?.tag?.model?.config?.tag?.ResourceConfigBuilder?.params?.ads?.enabled) {
            resource = this.setHTML5AdParameters(resource);
        }
        this.setViewabilityTracking(resource);
        resource = this.setMillstoneDAIParams(resource);
        resource = this.mergeParamsAdSettings(resource);
        resource = this.handleLinearAdTrackable(resource);
        return resource;
    }
    setHTML5AdParameters(resource) {
        const adParameterBuilder = new HTML5AdParameterBuilder({ tag: this.tag });
        const adParameters = adParameterBuilder.buildAdParameters();
        if (!this.utils.Type.isEmpty(adParameters)) {
            this.logger.log('setHTML5AdParameters', 'Setting HTML5 specific ad parameters', 'Params:', adParameters);
            Object.keys(adParameters).forEach((key) => {
                if (!this.utils.Type.isEmpty(adParameters[key])) {
                    resource = this.setSsaiAdParameter(resource, key, adParameters[key]);
                }
                else {
                    this.logger.warn('setHTML5AdParameters', 'Filtering out ad parameter with unset value...', 'param:', key);
                }
            });
        }
        return resource;
    }
    setDaiApiAdParameters(resource) {
        const stvUtilities = STVUtilities.getInstance();
        const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();
        const fwDaiApiRequest = this.createFreewheelRequest(adSetupInfo);
        const isContentLive = this.tag.model.contentUtilities.isLive(this.params, this?.tag?.model?.apiMetadata);
        const contentAssetKey = this.tag.model.contentUtilities.getDAIAssetKey(this.params, this?.tag?.model?.apiMetadata);
        resource = this.setApiKey(resource, '7j3n273bfj7jcnvf22d713a51f');
        resource = this.setAssetKey(resource, isContentLive ? contentAssetKey : null);
        resource = this.setAuthToken(resource, null);
        resource = this.setContentSourceId(resource, isContentLive ? null : stvUtilities.getContentSourceId(this.tag.model.contentUtilities.isDRM(this.params, this?.tag?.model?.apiMetadata)));
        resource = this.setVideoId(resource, isContentLive ? null : this?.tag?.model?.apiMetadata?.contentId);
        resource = this.setFilterOutPremiumAudioCodecs(resource, isContentLive ? true : stvUtilities.canFilterOutPremiumAudioCodecs(this?.params?.SYSTEM?.device));
        resource = this.setIsLive(resource, false);
        resource = this.setUseApi(resource, true);
        resource = this.setFormat(resource, stvUtilities.getFormat(this?.tag?.model?.apiMetadata));
        if (!this.utils.Type.isEmpty(fwDaiApiRequest.parameters)) {
            this?.logger?.log('setDaiApiAdParameters', 'setting Freewheel parameters as SSAI ad parameters...', 'Freewheel params:', fwDaiApiRequest.parameters);
            fwDaiApiRequest.parameters
                .filter((param) => {
                if (this.utils.Type.isEmpty(param.value)) {
                    this?.logger?.warn('setDaiApiAdParameters', 'filtering out Freewheel param with unset value...', 'param:', param);
                    return false;
                }
                if (this.html5ExcludedFMSKeys.includes(param.key)) {
                    this?.logger?.warn('setDaiApiAdParameters', 'filtering out excluded HTML5 param...', 'param:', param);
                    return false;
                }
                return true;
            })
                .forEach((param) => resource = this.setSsaiAdParameter(resource, param.key, param.value));
        }
        return resource;
    }
    handleLinearAdTrackable(resource) {
        if (!this.tag.model.contentUtilities.isLinearAdTrackable()) {
            this?.logger?.log('handleLinearAdTrackable', 'isLinearAdTrackable=false... returning resource unmodified');
            return resource;
        }
        else {
            this?.logger?.log('handleLinearAdTrackable', 'isLinearAdTrackable=true... set SB24 params in SSAI, remove CSAI');
            resource = this.setSuperBowl2024Parameter(resource);
            resource = this.removeCsai(resource);
            return resource;
        }
    }
    setSuperBowl2024Parameter(resource) {
        return this.setSsaiParameter(resource, 'superbowl2024', true);
    }
    setViewabilityTracking(resource) {
        const deviceType = this?.params?.SYSTEM?.device;
        const stvUtilities = STVUtilities.getInstance();
        const trackingObj = {
            partner: `pplus_${stvUtilities.getPlatformEnvToId(deviceType)}_app`
        };
        super.setViewabilityTracking(resource, trackingObj);
        return resource;
    }
    mergeParamsAdSettings(resource) {
        if (this.utils.Type.isEmpty(this.params?.AD?.SETTINGS) ||
            !this.utils.Type.isObject(this.params?.AD?.SETTINGS)) {
            this?.logger?.log('mergeParamsAdSettings', 'params.AD.SETTINGS is unset/invalid... returning resource unmodified');
            return resource;
        }
        else {
            this?.logger?.log('mergeParamsAdSettings', 'params.AD.SETTINGS is set... deep merging into resource.ad', '\nparams.AD.SETTINGS:', this.params?.AD?.SETTINGS);
            resource.ad = this.utils.DeepMerge.deepmerge(resource.ad, this.params?.AD?.SETTINGS);
            return resource;
        }
    }
}
